import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, CreditCard, Loader2, Lock, X } from 'lucide-react';
import { formatMoney } from '../../data/cemeteries';
import { createOrder, getPlot, getSelection, markOrderPaid } from '../../lib/plotStore';
import { SQUARE_ENVIRONMENT, createSquarePayment, isSquareConfigured } from '../../lib/square';
import { useSquareCard } from '../../lib/useSquareCard';

/**
 * Checkout: client details → payment → confirmation.
 *
 * Payment has two modes, chosen automatically:
 *  • Square (real) — active when REACT_APP_SQUARE_APPLICATION_ID / _LOCATION_ID
 *    are set. The card is entered in Square's hosted iframe, tokenized in the
 *    browser, and charged by our Cloudflare Pages Function (see
 *    functions/api/create-payment.js), which alone holds the secret token.
 *  • Demo (fallback) — when Square isn't configured, the original simulated
 *    flow runs so the site still works end to end. No real money moves and no
 *    card data leaves the browser.
 */

const BURIAL_LABEL = { burial: 'Burial', cremation: 'Cremation' };

/** Fresh idempotency key per pay attempt, so a fixed decline can be retried. */
const newIdempotencyKey = () =>
  (typeof window !== 'undefined' && window.crypto?.randomUUID?.()) ||
  `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Demo-mode stand-in for a real payment confirmation. Always succeeds. */
const processDemoPayment = () =>
  new Promise((resolve) => {
    window.setTimeout(
      () => resolve({ paymentRef: `demo_${Math.random().toString(36).slice(2, 12)}` }),
      1400,
    );
  });

const formatCardNumber = (value) =>
  value
    .replace(/\D/g, '')
    .slice(0, 16)
    .replace(/(.{4})/g, '$1 ')
    .trim();

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const EMPTY_CLIENT = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
};

const CheckoutModal = ({ cemetery, onClose, onComplete }) => {
  const [step, setStep] = useState('details'); // details | payment | done
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [notes, setNotes] = useState('');
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '', zip: '' });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const dialogRef = useRef(null);

  // Reserve the sites at most once — a retry after a decline reuses it rather
  // than creating a second pending order.
  const reservedOrderRef = useRef(null);

  const squareOn = isSquareConfigured();
  const {
    containerRef: cardContainerRef,
    ready: cardReady,
    error: cardError,
    tokenize,
  } = useSquareCard(squareOn && step === 'payment');

  // Snapshot the selection when checkout opens so the summary stays stable even
  // if a hold lapses while the client is typing. The authoritative re-check
  // happens in createOrder().
  const [plots] = useState(() =>
    getSelection(cemetery.id).map((id) => getPlot(cemetery, id)),
  );

  const subtotal = plots.reduce((sum, p) => sum + p.price, 0);
  const careFee = Math.round(subtotal * cemetery.pricing.careFeeRate);
  const total = subtotal + careFee;

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && step !== 'done' && !processing && onClose();
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step, processing]);

  const setField = (field) => (e) => setClient((c) => ({ ...c, [field]: e.target.value }));

  const detailsValid =
    client.firstName.trim() &&
    client.lastName.trim() &&
    /\S+@\S+\.\S+/.test(client.email) &&
    client.phone.trim();

  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    if (!detailsValid) {
      setError('Please complete your name, email, and phone number.');
      return;
    }
    setError(null);
    setStep('payment');
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setError(null);
    setProcessing(true);

    // Reserve the sites first — re-validates the holds and fails cleanly if one
    // lapsed while the form was open.
    let reserved = reservedOrderRef.current;
    if (!reserved) {
      const result = createOrder(cemetery, { client, notes });
      if (!result.ok) {
        setProcessing(false);
        setError(result.reason);
        return;
      }
      reserved = result.order;
      reservedOrderRef.current = reserved;
    }

    try {
      let paymentRef;

      if (squareOn) {
        const token = await tokenize();
        const result = await createSquarePayment({
          sourceId: token,
          cemeteryId: cemetery.id,
          plotIds: plots.map((p) => p.id),
          idempotencyKey: newIdempotencyKey(),
          buyer: {
            email: client.email,
            givenName: client.firstName,
            familyName: client.lastName,
          },
          note: notes,
        });
        paymentRef = result.paymentId;
      } else {
        const payment = await processDemoPayment();
        paymentRef = payment.paymentRef;
      }

      const paid = markOrderPaid(reserved.id, paymentRef);
      setProcessing(false);
      setOrder(paid.order);
      setStep('done');
      onComplete?.(paid.order);
    } catch (err) {
      setProcessing(false);
      // The sites stay reserved (the order exists) — surface why and let them
      // retry with a corrected card.
      setError(err?.message || 'We could not process that payment. Your sites remain reserved.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-2xl focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 p-6">
          <div>
            <h2
              id="checkout-title"
              className="font-cormorant text-3xl font-semibold text-navy-900"
            >
              {step === 'done' ? 'Your sites are reserved' : 'Complete your purchase'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{cemetery.name}</p>
          </div>
          {step !== 'done' && (
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
              aria-label="Close checkout"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Payment-mode notice */}
        {step === 'payment' && !squareOn && (
          <div className="mx-6 mt-6 flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-relaxed text-amber-800">
              <strong>Demo mode.</strong> No payment processor is connected. Nothing is charged,
              no card details are stored or transmitted, and checkout completes with any input.
            </p>
          </div>
        )}
        {step === 'payment' && squareOn && SQUARE_ENVIRONMENT !== 'production' && (
          <div className="mx-6 mt-6 flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-relaxed text-amber-800">
              <strong>Square Sandbox.</strong> Use test card{' '}
              <strong>4111&nbsp;1111&nbsp;1111&nbsp;1111</strong>, any future expiry, CVV{' '}
              <strong>111</strong>, ZIP <strong>94103</strong>. No real money moves.
            </p>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-6 flex gap-3 rounded-md border border-red-200 bg-red-50 p-3">
            <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
            <p className="text-xs leading-relaxed text-red-700">{error}</p>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 'details' && (
          <form onSubmit={handleDetailsSubmit} className="p-6">
            <OrderSummary plots={plots} subtotal={subtotal} careFee={careFee} total={total} />

            <h3 className="mb-4 mt-8 font-cormorant text-2xl font-semibold text-navy-900">
              Your details
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="First name" required value={client.firstName} onChange={setField('firstName')} />
              <Field label="Last name" required value={client.lastName} onChange={setField('lastName')} />
              <Field label="Email" type="email" required value={client.email} onChange={setField('email')} />
              <Field label="Phone" type="tel" required value={client.phone} onChange={setField('phone')} />
              <div className="sm:col-span-2">
                <Field label="Street address" value={client.street} onChange={setField('street')} />
              </div>
              <Field label="City" value={client.city} onChange={setField('city')} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="State" value={client.state} onChange={setField('state')} />
                <Field label="ZIP" value={client.zip} onChange={setField('zip')} />
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                    Notes for our staff
                  </span>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything we should know — family plots to keep together, marker preferences, veteran status."
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-500 focus:border-navy-700 focus:outline-none focus:ring-1 focus:ring-navy-700"
                  />
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="mt-6 w-full rounded-md bg-navy-800 py-3 text-sm font-medium tracking-wide text-white transition hover:bg-navy-900"
            >
              Continue to payment
            </button>
          </form>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 'payment' && (
          <form onSubmit={handlePay} className="p-6">
            <OrderSummary plots={plots} subtotal={subtotal} careFee={careFee} total={total} />

            <h3 className="mb-4 mt-8 flex items-center gap-2 font-cormorant text-2xl font-semibold text-navy-900">
              <CreditCard className="h-5 w-5" />
              Payment
            </h3>

            {squareOn ? (
              <div>
                <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                  Card details
                </span>
                {/* Square injects its secure card iframe here. */}
                <div ref={cardContainerRef} className="min-h-[46px]" />
                {!cardReady && !cardError && (
                  <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading secure card field…
                  </p>
                )}
                {cardError && <p className="mt-2 text-xs text-red-600">{cardError}</p>}
                <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
                  <Lock className="h-3 w-3" />
                  Encrypted and processed by Square — we never see your card number.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field
                    label="Card number"
                    placeholder="4242 4242 4242 4242"
                    inputMode="numeric"
                    autoComplete="off"
                    value={card.number}
                    onChange={(e) =>
                      setCard((c) => ({ ...c, number: formatCardNumber(e.target.value) }))
                    }
                  />
                </div>
                <Field
                  label="Expiry"
                  placeholder="MM/YY"
                  inputMode="numeric"
                  autoComplete="off"
                  value={card.expiry}
                  onChange={(e) => setCard((c) => ({ ...c, expiry: formatExpiry(e.target.value) }))}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Field
                    label="CVC"
                    placeholder="123"
                    inputMode="numeric"
                    autoComplete="off"
                    value={card.cvc}
                    onChange={(e) =>
                      setCard((c) => ({ ...c, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) }))
                    }
                  />
                  <Field
                    label="ZIP"
                    placeholder="20772"
                    inputMode="numeric"
                    autoComplete="off"
                    value={card.zip}
                    onChange={(e) =>
                      setCard((c) => ({ ...c, zip: e.target.value.replace(/\D/g, '').slice(0, 5) }))
                    }
                  />
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setStep('details')}
                disabled={processing}
                className="rounded-md border border-slate-300 px-5 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={processing || (squareOn && !cardReady)}
                className="flex flex-1 items-center justify-center gap-2 rounded-md bg-navy-800 py-3 text-sm font-medium tracking-wide text-white transition hover:bg-navy-900 disabled:opacity-70"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Pay {formatMoney(total)}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* ---------------------------------------------------------------- */}
        {step === 'done' && order && (
          <div className="p-6">
            <div className="flex flex-col items-center py-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-navy-700" />
              <p className="mt-4 text-sm text-slate-600">
                Thank you, {order.client.firstName}. Your order is confirmed and our staff will
                reach out within one business day to complete the deed paperwork.
              </p>
              <p className="mt-4 font-cormorant text-2xl font-semibold text-navy-900">
                Order {order.id}
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <OrderSummary
                plots={plots}
                subtotal={order.subtotal}
                careFee={order.careFee}
                total={order.total}
              />
            </div>

            <p className="mt-4 text-center text-[11px] text-slate-500">
              A confirmation will be emailed to {order.client.email}.
            </p>

            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full rounded-md bg-navy-800 py-3 text-sm font-medium tracking-wide text-white transition hover:bg-navy-900"
            >
              Back to the site map
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const OrderSummary = ({ plots, subtotal, careFee, total }) => (
  <div>
    <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500">
      Order summary
    </h3>
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
      {plots.map((plot) => (
        <li key={plot.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
          <span className="text-slate-700">
            <strong className="font-medium">Site {plot.id}</strong>
            <span className="ml-2 text-xs text-slate-500">
              Row {plot.row}, No. {plot.number} · {BURIAL_LABEL[plot.burialType]}
            </span>
          </span>
          <span className="tabular-nums text-slate-700">{formatMoney(plot.price)}</span>
        </li>
      ))}
    </ul>
    <dl className="mt-3 space-y-1.5 text-sm">
      <div className="flex justify-between text-slate-600">
        <dt>Subtotal</dt>
        <dd className="tabular-nums">{formatMoney(subtotal)}</dd>
      </div>
      <div className="flex justify-between text-slate-600">
        <dt>Perpetual care</dt>
        <dd className="tabular-nums">{formatMoney(careFee)}</dd>
      </div>
      <div className="flex justify-between border-t border-slate-100 pt-1.5 font-semibold text-navy-900">
        <dt>Total</dt>
        <dd className="tabular-nums">{formatMoney(total)}</dd>
      </div>
    </dl>
  </div>
);

const Field = ({ label, required, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
      {label}
      {required && <span className="ml-0.5 text-navy-700">*</span>}
    </span>
    <input
      {...props}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-500 focus:border-navy-700 focus:outline-none focus:ring-1 focus:ring-navy-700"
    />
  </label>
);

export default CheckoutModal;
